import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Copy, CreditCard, Loader2, Send } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { SandboxNotice } from "@/components/SandboxNotice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** Ask for money on a trip at any time — full balance by default, any amount if you type one. */
export const PaymentLinkCard = ({ manifestId }) => {
  const [state, setState] = useState(null);
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState("");
  const [allowTip, setAllowTip] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    api.get(`/manifests/${manifestId}/payment-link`)
      .then(({ data }) => {
        setState(data);
        setAmount(data.outstanding > 0 ? String(data.outstanding) : "");
        setEmail(data.customer_email || "");
      })
      .catch(() => {});
  }, [manifestId]);
  useEffect(() => { load(); }, [load]);

  const send = async (sendEmail) => {
    setBusy(true); setError(""); setMsg("");
    try {
      const { data } = await api.post(`/manifests/${manifestId}/payment-link`, {
        amount: Number(amount) || null, email, send_email: sendEmail, allow_tip: allowTip,
      });
      setMsg(data.emailed_to
        ? `Payment link for ${money(data.amount)} emailed to ${data.emailed_to}.`
        : `Payment link for ${money(data.amount)} created — copy it below.`);
      load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const chargeCard = async () => {
    setBusy(true); setError(""); setMsg("");
    try {
      const { data } = await api.post(`/manifests/${manifestId}/charge-card`);
      setMsg(`${money(data.amount)} charged to the card on file.`);
      load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const requestCard = async () => {
    setBusy(true); setError(""); setMsg("");
    try {
      const { data } = await api.post(`/manifests/${manifestId}/request-card`, {
        email, send_email: !!email,
      });
      setMsg(data.emailed_to
        ? `Card request emailed to ${data.emailed_to}. The balance settles itself once they add it.`
        : "Card request link created — copy it below and text it to the passenger.");
      load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  if (!state) return null;
  const link = state.last_link;
  const request = state.card_request || {};
  const noCardYet = state.payments_ready && state.auto_charge?.on
    && !state.card_on_file?.last4 && state.processor === "stripe";

  return (
    <div data-testid="payment-link-card" className="no-print rounded-xl border border-ink-600 bg-ink-800 p-5 sm:p-6">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
        <CreditCard className="h-3.5 w-3.5" /> Take payment by card
      </p>

      {!state.payments_ready ? (
        <p data-testid="payment-link-not-ready" className="mt-3 text-sm text-slate-400">
          Connect your Stripe account in Settings → Payments and you can send a card link
          for any balance straight from this trip.
        </p>
      ) : (
        <>
          <p className="mt-2 text-sm text-slate-400">
            {state.outstanding > 0
              ? `${money(state.outstanding)} is outstanding on this trip.`
              : "Nothing outstanding — you can still charge any amount, for a change or extra stop."}
            {state.tip_amount > 0 && (
              <span data-testid="payment-link-tip-collected" className="text-emerald-300">
                {" "}Gratuity collected: {money(state.tip_amount)}.
              </span>
            )}
          </p>
          {error && <p data-testid="payment-link-error" className="mt-3 text-sm text-red-400">{error}</p>}
          {msg && <p data-testid="payment-link-msg" className="mt-3 text-sm text-emerald-400">{msg}</p>}

          {state.payments_ready && (state.card_on_file?.last4 || state.auto_charge?.status) && (
            <div data-testid="payment-card-on-file" className="mt-4 rounded-lg border border-ink-600 bg-ink-900/60 p-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                Card on file
              </p>
              <p className="mt-1 text-sm text-slate-200">
                {state.card_on_file?.last4
                  ? `${state.card_on_file.brand || "Card"} ···· ${state.card_on_file.last4} — the passenger agreed to settle the balance on it.`
                  : "No card kept for this trip."}
              </p>
              {state.auto_charge?.status === "armed" && (
                <p data-testid="payment-auto-armed" className="mt-1 text-xs text-emerald-300">
                  {money(state.outstanding)} will be charged automatically about{" "}
                  {state.auto_charge.grace_minutes} minutes after the trip — adjust the fare before
                  then and the new amount is taken instead.
                </p>
              )}
              {state.auto_charge?.status === "paid" && (
                <p data-testid="payment-auto-paid" className="mt-1 text-xs text-emerald-300">
                  {money(state.auto_charge.amount)} already settled on this card.
                </p>
              )}
              {state.auto_charge?.status === "failed" && (
                <p data-testid="payment-auto-failed" className="mt-1 text-xs text-red-300">
                  The card was declined ({state.auto_charge.error || "no reason given"}). A payment
                  link has been emailed — you can also try the card again.
                </p>
              )}
              {state.card_on_file?.last4 && state.outstanding > 0 && (
                <Button data-testid="payment-charge-now" variant="outline" disabled={busy}
                  onClick={chargeCard}
                  className="mt-3 h-11 border-gold/40 bg-transparent text-gold hover:bg-gold/10">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <><CreditCard className="mr-2 h-4 w-4" /> Charge {money(state.outstanding)} to this card now</>}
                </Button>
              )}
            </div>
          )}

          {noCardYet && (
            <div data-testid="payment-no-card" className="mt-4 rounded-lg border border-ink-600 bg-ink-900/60 p-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                No card on file
              </p>
              <p className="mt-1 text-sm text-slate-300">
                Nothing can be charged automatically after this trip until the passenger stores a
                card. Send them a secure link — it takes no money, and once a card is saved the
                balance settles by itself about {state.auto_charge.grace_minutes} minutes after the
                trip.
              </p>
              {request.status === "pending" && (
                <p data-testid="payment-card-request-pending" className="mt-2 text-xs text-amber-300">
                  Asked{request.emailed_to ? ` ${request.emailed_to}` : ""} — waiting for them to add
                  it. Pressing again re-sends the same link.
                </p>
              )}
              <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                <Button data-testid="payment-request-card" variant="outline" disabled={busy}
                  onClick={requestCard}
                  className="h-11 border-gold/40 bg-transparent text-gold hover:bg-gold/10">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <><CreditCard className="mr-2 h-4 w-4" />
                      {request.status === "pending" ? "Re-send card request" : "Ask for a card on file"}</>}
                </Button>
                {request.url && (
                  <button data-testid="payment-card-request-copy" type="button"
                    onClick={() => { navigator.clipboard?.writeText(request.url); setMsg("Card link copied — text it to the passenger."); }}
                    className="flex items-center gap-1.5 font-mono text-[11px] text-slate-500 hover:text-gold">
                    <Copy className="h-3.5 w-3.5" /> Copy the link instead
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Amount</label>
              <Input data-testid="payment-link-amount" type="number" step="0.01" min="1" value={amount}
                onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
                className="h-11 border-ink-600 bg-ink-900 text-slate-100" />
            </div>
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Send to</label>
              <Input data-testid="payment-link-email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="passenger@email.com"
                className="h-11 border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600" />
            </div>
          </div>

          <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-ink-600 bg-ink-900/60 p-3">
            <input data-testid="payment-link-allow-tip" type="checkbox" checked={allowTip}
              onChange={(e) => setAllowTip(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[#D4AF37]" />
            <span className="text-xs leading-relaxed text-slate-300">
              Let the passenger add a gratuity (18 / 20 / 25% or their own amount) before they pay.
              <span className="block text-slate-500">100% of it goes to the chauffeur's payout.</span>
            </span>
          </label>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <Button data-testid="payment-link-send" disabled={busy || !Number(amount)} onClick={() => send(true)}
              className="h-12 bg-gold text-ink-900 hover:bg-gold-light">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Email payment link</>}
            </Button>
            <Button data-testid="payment-link-create" variant="outline" disabled={busy || !Number(amount)}
              onClick={() => send(false)}
              className="h-12 border-ink-600 bg-transparent text-slate-300 hover:text-gold">
              Just create the link
            </Button>
          </div>

          {state.sandbox && (
            <div className="mt-4">
              <SandboxNotice testId="payment-link-sandbox" />
            </div>
          )}

          {link && (
            <div data-testid="payment-link-last" className="mt-5 border-t border-ink-600 pt-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                Last link · {money(link.amount)}
                {link.emailed_to ? ` · emailed to ${link.emailed_to}` : ""}
                {link.allow_tip ? " · tip enabled" : ""}
              </p>
              {link.paid_at ? (
                <p data-testid="payment-link-paid" className="mt-2 flex items-center gap-2 text-sm text-emerald-300">
                  <CheckCircle2 className="h-4 w-4" /> Paid {money(link.charged || link.amount)}
                  {link.tip > 0 ? ` including a ${money(link.tip)} gratuity` : ""}
                </p>
              ) : (
                <div className="mt-2 flex items-center gap-2">
                  <a data-testid="payment-link-url" href={link.url} target="_blank" rel="noreferrer"
                    className="truncate font-mono text-[11px] text-gold hover:underline">
                    {link.url}
                  </a>
                  <button data-testid="payment-link-copy" title="Copy link"
                    onClick={() => { navigator.clipboard?.writeText(link.url); setMsg("Link copied — text it to the passenger."); }}
                    className="text-slate-500 hover:text-gold">
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
